import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { X, Search, Mail, Calendar, Trash2, Plus, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ['Call', 'Meeting', 'Interview', 'Follow-up', 'Other'];

export default function ScheduleEventModal({
  open,
  onClose,
  relatedType,        // 'personnel' | 'applicant' | null
  relatedId,          // string | null
  defaultAttendee,    // { kind, ref_id, name, email } | null  -- the page's owner pre-filled
  initialEvent,       // existing event for edit; null for create
  onSaved,
}) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('Meeting');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [sendInvites, setSendInvites] = useState(true);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [reminders, setReminders] = useState([]);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Reset / hydrate ONLY on the open=false -> open=true transition.
  // Parent re-renders (e.g., notification polls) can change defaultAttendee/initialEvent
  // identity without us actually re-opening — those must NOT clobber the user's draft.
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return; // already hydrated for this open cycle
    wasOpenRef.current = true;

    if (initialEvent) {
      setTitle(initialEvent.title || '');
      setType(initialEvent.type || 'Meeting');
      const sa = initialEvent.start_at || '';
      setDate(sa.slice(0, 10));
      setTime(sa.slice(11, 16));
      setDuration(initialEvent.duration_minutes || 30);
      setLocation(initialEvent.location || '');
      setDescription(initialEvent.description || '');
      setAttendees(initialEvent.attendees || []);
      setReminders(initialEvent.reminders || []);
      setSendInvites(false);
    } else {
      setTitle('');
      setType('Meeting');
      setDate('');
      setTime('');
      setDuration(30);
      setLocation('');
      setDescription('');
      setReminders([30]);  // sensible default: 30 min before
      setSendInvites(true);
      // pre-add owner of the page
      if (defaultAttendee && defaultAttendee.ref_id) {
        setAttendees([defaultAttendee]);
      } else {
        setAttendees([]);
      }
    }
    setSearch('');
    setResults([]);
    setExternalName('');
    setExternalEmail('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced people search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/events/search/people`, { params: { q: search, limit: 20 } });
        setResults(res.data || []);
      } catch (_) { setResults([]); }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, open]);

  const addAttendee = (person) => {
    if (attendees.some(a => a.kind === person.kind && a.ref_id === person.ref_id)) return;
    setAttendees([...attendees, { kind: person.kind, ref_id: person.ref_id, name: person.name, email: person.email }]);
  };

  const removeAttendee = (idx) => {
    setAttendees(attendees.filter((_, i) => i !== idx));
  };

  const addExternal = () => {
    const nm = externalName.trim();
    const em = externalEmail.trim();
    if (!nm && !em) {
      toast.error('Add a name or email for the external attendee');
      return;
    }
    if (em && !em.includes('@')) {
      toast.error('Email looks invalid');
      return;
    }
    setAttendees([...attendees, { kind: 'external', ref_id: null, name: nm || em, email: em || null }]);
    setExternalName('');
    setExternalEmail('');
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!date) { toast.error('Date is required'); return; }
    if (!time) { toast.error('Time is required'); return; }
    setSaving(true);
    const payload = {
      title: title.trim(),
      type,
      start_at: `${date}T${time}`,
      duration_minutes: parseInt(duration) || 30,
      location: location.trim() || null,
      description: description.trim() || null,
      attendees,
      related_type: relatedType || undefined,
      related_id: relatedId || undefined,
      send_invites: sendInvites,
      timezone: 'America/Chicago',
      reminders,
    };
    try {
      let saved;
      if (initialEvent && initialEvent.id) {
        const res = await axios.put(`${API}/events/${initialEvent.id}`, payload);
        saved = res.data;
        toast.success('Event updated');
      } else {
        const res = await axios.post(`${API}/events`, payload);
        saved = res.data;
        toast.success(sendInvites ? 'Event scheduled — invites sent' : 'Event scheduled');
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="schedule-event-modal">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calendar size={18} /> {initialEvent ? 'Edit Event' : 'Schedule Event'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-event-modal">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Initial Phone Screen" data-testid="event-title-input" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Type</Label>
              <select value={type} onChange={e => setType(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="event-type-select">
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="event-date-input" />
            </div>
            <div>
              <Label>Time *</Label>
              <Input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="event-time-input" />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" min={5} step={5} value={duration} onChange={e => setDuration(e.target.value)} data-testid="event-duration-input" />
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1"><MapPin size={12} /> Location / Video Link</Label>
            <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="123 Main St, Tyler TX  —  or  https://meet.google.com/..." data-testid="event-location-input" />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Agenda, notes, dial-in details…" rows={3} data-testid="event-description-input" />
          </div>

          {/* Reminders */}
          <div>
            <Label className="flex items-center gap-1"><Clock size={12} /> Reminders</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {[
                { v: 5, l: '5 min' },
                { v: 15, l: '15 min' },
                { v: 30, l: '30 min' },
                { v: 60, l: '1 hr' },
                { v: 120, l: '2 hrs' },
                { v: 1440, l: '1 day' },
                { v: 2880, l: '2 days' },
                { v: 10080, l: '1 week' },
              ].map(opt => {
                const active = reminders.includes(opt.v);
                return (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setReminders(active ? reminders.filter(x => x !== opt.v) : [...reminders, opt.v].sort((a,b)=>b-a))}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${active ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-input hover:bg-muted'}`}
                    data-testid={`reminder-${opt.v}`}
                  >
                    {active ? '✓ ' : '+ '}{opt.l} before
                  </button>
                );
              })}
            </div>
            {reminders.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Email reminders will be sent {reminders.length === 1 ? 'once' : `${reminders.length} times`} to attendees with email addresses.
              </p>
            )}
          </div>

          {/* Attendees */}
          <div className="border rounded-lg p-3 bg-muted/20">
            <Label className="text-sm font-semibold mb-2 block">Attendees ({attendees.length})</Label>

            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {attendees.map((a, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-white border rounded-full px-2 py-1 text-xs" data-testid={`attendee-chip-${i}`}>
                    <span className={`w-2 h-2 rounded-full ${a.kind === 'personnel' ? 'bg-blue-500' : a.kind === 'applicant' ? 'bg-amber-500' : 'bg-gray-400'}`} />
                    <span className="font-medium">{a.name}</span>
                    {a.email && <span className="text-muted-foreground">&lt;{a.email}&gt;</span>}
                    <button onClick={() => removeAttendee(i)} className="text-muted-foreground hover:text-red-500"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="relative mb-2">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search personnel or applicants…"
                className="pl-7 h-9 text-sm"
                data-testid="attendee-search-input"
              />
            </div>
            {(results.length > 0) && (
              <div className="border rounded-md max-h-40 overflow-y-auto bg-white mb-3">
                {results.map(r => {
                  const already = attendees.some(a => a.kind === r.kind && a.ref_id === r.ref_id);
                  return (
                    <button
                      key={`${r.kind}-${r.ref_id}`}
                      type="button"
                      onClick={() => addAttendee(r)}
                      disabled={already}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 disabled:opacity-40 flex items-center justify-between border-b last:border-b-0"
                      data-testid={`search-result-${r.ref_id}`}
                    >
                      <div>
                        <p className="font-medium">{r.name}</p>
                        <p className="text-xs text-muted-foreground">{r.subtitle} {r.email ? `· ${r.email}` : ''}</p>
                      </div>
                      {already ? <span className="text-xs text-muted-foreground">added</span> : <Plus size={14} />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* External attendee */}
            <div className="border-t pt-2 mt-2">
              <p className="text-xs text-muted-foreground mb-1">Or add someone not in the system:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={externalName} onChange={e => setExternalName(e.target.value)} placeholder="Name" className="h-9 text-sm" data-testid="external-name-input" />
                <Input value={externalEmail} onChange={e => setExternalEmail(e.target.value)} placeholder="email@example.com" className="h-9 text-sm" data-testid="external-email-input" />
                <Button variant="outline" size="sm" onClick={addExternal} data-testid="add-external-btn">
                  <Plus size={12} className="mr-1" /> Add external
                </Button>
              </div>
            </div>
          </div>

          {/* Send invites toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendInvites} onChange={e => setSendInvites(e.target.checked)} data-testid="send-invites-checkbox" />
            <Mail size={13} /> Email invites to all attendees ({attendees.filter(a => a.email).length} will receive)
          </label>

          <div className="flex gap-2 justify-end pt-3 border-t">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn">
              {saving ? 'Saving…' : (initialEvent ? 'Save Changes' : 'Schedule Event')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
